import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import ordersService, {
  type AllDispatcher,
  type ChassisEquipment,
  type VanEquipment,
} from '../services/orders.service';
import api from '../services/api';
import { cachedGet } from '../utils/lookupCache';
import { useResolvedAccessLevel } from '../components/PermissionsContainer';
import { useWorkingStatusPreference } from './WorkingStatusPreferenceContext';

export interface CsrOption {
  value: string;
  label: string;
}

export interface OrderFormContextValue {
  allDispatchers: AllDispatcher[];
  csrs: CsrOption[];
  chassisProviders: string[];
  chassisEquipment: ChassisEquipment[];
  vanEquipment: VanEquipment[];
  dispatchersLoading: boolean;
  providersLoading: boolean;
  equipmentLoading: boolean;
  legEditingAccess: number;
  arrivalDepartAccess: number;
  driverAccess: number;
  /** True when the user may edit a DELIVERY leg's location. Gated by the
   *  `leg-delivery-location` component permission (ships disabled until an admin
   *  grants a group Edit). Non-delivery leg locations follow legEditingAccess. */
  canEditDeliveryLocation: boolean;
  /** Resolved `leg-appt-clear` level — gates the X beside every appt-date cell.
   *  Ships at level 1 (visible, disabled) so the affordance is discoverable
   *  before an admin grants Edit. See canClearAppt in utils/legFieldGuards. */
  apptClearAccess: number;
  /** Resolved `leg-appt-lock-override` level. At >= 2 the PICKUP/DELIVERY appt
   *  lock doesn't apply to this user at all — dispatch edits agreed appointments
   *  in place, and the clear button waives its driver-blank requirement. Does not
   *  lift the paid-leg locks. */
  apptLockOverrideAccess: number;
  /** Resolved `leg-appt-edit` level. At >= 2 the PICKUP/DELIVERY appt lock is
   *  lifted while the dispatch scope carries NO driver — the warehouse-LC grant:
   *  move an appointment you set yourself until the move is dispatched. Unlike
   *  apptLockOverrideAccess it stops at a driver, and it never lifts a paid lock. */
  apptEditAccess: number;
  defaultSendToGwiMove: boolean;
  effectiveDispatcher: string;
  effectiveDispatcherIsGwi: boolean;
  setEffectiveDispatcherInputs: (args: {
    editedDispatcher?: string;
    savedDispatcher?: string;
    savedDispatcherIsGwi?: boolean;
  }) => void;
}

const OrderFormContext = createContext<OrderFormContextValue | undefined>(undefined);

export const OrderFormProvider = ({ children }: { children: ReactNode }) => {
  const isAuthenticated = useIsAuthenticated();
  const isDevelopment = import.meta.env.DEV;
  const bypassAuth = isDevelopment && import.meta.env.VITE_BYPASS_AUTH === 'true';

  const [allDispatchers, setAllDispatchers] = useState<AllDispatcher[]>([]);
  const [csrs, setCsrs] = useState<CsrOption[]>([]);
  const [chassisProviders, setChassisProviders] = useState<string[]>([]);
  const [chassisEquipment, setChassisEquipment] = useState<ChassisEquipment[]>([]);
  const [vanEquipment, setVanEquipment] = useState<VanEquipment[]>([]);
  const [dispatchersLoading, setDispatchersLoading] = useState(false);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [equipmentLoading, setEquipmentLoading] = useState(false);

  const legEditingAccess = useResolvedAccessLevel('leg-editing');
  const arrivalDepartAccess = useResolvedAccessLevel('leg-arrival-depart');
  // Editing a leg's driver assignment requires an explicit grant: `leg-driver-field`
  // ships **defaultDisabled**, so un-configured groups resolve to level 1 (View —
  // driver cell read-only) rather than level 2 (Edit). Admins grant Edit to the
  // dispatcher groups. Keep this fallback in sync with useCanEditDriverField.
  const driverAccess = useResolvedAccessLevel('leg-driver-field', false, true);
  // Delivery-leg location editing is a separately-granted capability (Managers
  // yes, LCs no by default). defaultDisabled → un-granted groups resolve to
  // level 1, so `>= 2` is false until an admin grants Edit.
  const canEditDeliveryLocation =
    useResolvedAccessLevel('leg-delivery-location', false, true) >= 2;
  // Clearing an appt date on a PICKUP/DELIVERY scope is the only way to change
  // it from the grid, so it ships behind its own grant. defaultDisabled → level
  // 1 for un-configured groups: the X renders, disabled.
  const apptClearAccess = useResolvedAccessLevel('leg-appt-clear', false, true);
  // Elevated: edit an already-agreed appointment in place. Granted to dispatch.
  const apptLockOverrideAccess = useResolvedAccessLevel(
    'leg-appt-lock-override',
    false,
    true,
  );
  // Warehouse LCs: edit an appointment they set, until a driver is on it.
  // defaultDisabled → un-granted groups resolve to level 1 and nothing changes.
  const apptEditAccess = useResolvedAccessLevel('leg-appt-edit', false, true);
  const { defaultSendToGwiMove } = useWorkingStatusPreference();

  type DispatcherInputs = {
    editedDispatcher?: string;
    savedDispatcher?: string;
    savedDispatcherIsGwi?: boolean;
  };
  const [dispatcherInputs, setDispatcherInputs] = useState<DispatcherInputs>({});

  /**
   * Replace the dispatcher inputs in full. Callers (the drawer's
   * `OrderDetailsDrawerContent` and `/pro`'s `OrderFormView`) must
   * always pass all three keys; `undefined` means "no value" — e.g.,
   * `editedDispatcher: undefined` correctly transmits "user has no
   * pending edit," and the previous edit is dropped.
   *
   * NOTE: Mutual exclusion assumed — only one consumer should be
   * mounted at a time. The drawer and `/pro` are routed independently
   * so this holds today. If a future layout embeds both surfaces
   * simultaneously, last-writer-wins becomes a bug.
   */
  const setEffectiveDispatcherInputs = useCallback((args: DispatcherInputs) => {
    setDispatcherInputs(args);
  }, []);

  useEffect(() => {
    if (!isAuthenticated && !bypassAuth) return;
    setDispatchersLoading(true);
    setProvidersLoading(true);
    setEquipmentLoading(true);
    const dispCtl = new AbortController();
    const provCtl = new AbortController();
    const eqCtl = new AbortController();
    const vanCtl = new AbortController();
    const csrCtl = new AbortController();
    ordersService
      .getAllDispatchers(dispCtl.signal)
      .then(({ data }) => setAllDispatchers(data))
      .catch((err: any) => {
        if (err?.code !== 'ERR_CANCELED' && err?.name !== 'CanceledError') {
          console.error('[OrderFormProvider] getAllDispatchers failed', err);
        }
      })
      .finally(() => setDispatchersLoading(false));
    cachedGet<CsrOption[]>('/cms-lookups/csrs', { signal: csrCtl.signal })
      .then(({ data }) => setCsrs(data))
      .catch((err: any) => {
        if (err?.code !== 'ERR_CANCELED' && err?.name !== 'CanceledError') {
          console.error('[OrderFormProvider] getCsrs failed', err);
        }
      });
    ordersService
      .getChassisProviders(provCtl.signal)
      .then(setChassisProviders)
      .catch((err: any) => {
        if (err?.code !== 'ERR_CANCELED' && err?.name !== 'CanceledError') {
          console.error('[OrderFormProvider] getChassisProviders failed', err);
        }
      })
      .finally(() => setProvidersLoading(false));
    ordersService
      .getChassisEquipment(eqCtl.signal)
      .then(setChassisEquipment)
      .catch((err: any) => {
        if (err?.code !== 'ERR_CANCELED' && err?.name !== 'CanceledError') {
          console.error('[OrderFormProvider] getChassisEquipment failed', err);
        }
      })
      .finally(() => setEquipmentLoading(false));
    ordersService
      .getVanEquipment(vanCtl.signal)
      .then(setVanEquipment)
      .catch((err: any) => {
        if (err?.code !== 'ERR_CANCELED' && err?.name !== 'CanceledError') {
          console.error('[OrderFormProvider] getVanEquipment failed', err);
        }
      });
    return () => {
      dispCtl.abort();
      provCtl.abort();
      eqCtl.abort();
      vanCtl.abort();
      csrCtl.abort();
    };
  }, [isAuthenticated, bypassAuth]);

  const effectiveDispatcher = useMemo(
    () => dispatcherInputs.editedDispatcher ?? dispatcherInputs.savedDispatcher ?? '',
    [dispatcherInputs.editedDispatcher, dispatcherInputs.savedDispatcher],
  );
  const effectiveDispatcherIsGwi = useMemo(
    () =>
      allDispatchers.find((d) => d.username === effectiveDispatcher)?.gwi ??
      dispatcherInputs.savedDispatcherIsGwi ??
      true,
    [allDispatchers, effectiveDispatcher, dispatcherInputs.savedDispatcherIsGwi],
  );

  const value = useMemo<OrderFormContextValue>(
    () => ({
      allDispatchers,
      csrs,
      chassisProviders,
      chassisEquipment,
      vanEquipment,
      dispatchersLoading,
      providersLoading,
      equipmentLoading,
      legEditingAccess,
      arrivalDepartAccess,
      driverAccess,
      canEditDeliveryLocation,
      apptClearAccess,
      apptLockOverrideAccess,
      apptEditAccess,
      defaultSendToGwiMove,
      effectiveDispatcher,
      effectiveDispatcherIsGwi,
      setEffectiveDispatcherInputs,
    }),
    [
      allDispatchers,
      csrs,
      chassisProviders,
      chassisEquipment,
      vanEquipment,
      dispatchersLoading,
      providersLoading,
      equipmentLoading,
      legEditingAccess,
      arrivalDepartAccess,
      driverAccess,
      canEditDeliveryLocation,
      apptClearAccess,
      apptLockOverrideAccess,
      apptEditAccess,
      defaultSendToGwiMove,
      effectiveDispatcher,
      effectiveDispatcherIsGwi,
      setEffectiveDispatcherInputs,
    ],
  );
  return <OrderFormContext.Provider value={value}>{children}</OrderFormContext.Provider>;
};

export const useOrderFormContext = (): OrderFormContextValue => {
  const ctx = useContext(OrderFormContext);
  if (!ctx) throw new Error('useOrderFormContext must be used inside OrderFormProvider');
  return ctx;
};
